import {FrameAction, FrameActions} from '@mc/react-frame';
import {
    Coordinate,
    FeatureFilter,
    FeatureId,
    ZoomOptions,
    PrintConfigType,
    FieldProps,
    Params,
    LayerId,
} from '../types';
import {FeatureIdType, FeatureFilterType} from '../enums';
import * as InternalActions from './InternalActions';
import {Dispatch} from 'react';
import {Feature} from 'geojson';
import {LayerSelectTags} from '../types/LayerSelectTags';

/**
 * Akce nad mapovou komponentou.
 */
export interface MapActions {
    /**
     * Nastaví téma konfigurace.
     *
     * @param topic - Téma konfigurace.
     * @returns Akce.
     */
    setTopic: (topic: string) => void;

    /**
     * Upravení topicu, pokud bude "topic" prázdný, aktualizujou se všechny.
     *
     * @param topic - Téma konfigurace.
     * @returns Akce.
     */
    updateTopic: (topic?: string) => void;

    /**
     * Nastaví podkladovou vrstvu.
     *
     * @param layerId - ID vrstvy nebo `null` pro její vypnutí.
     * @returns Akce.
     */
    setBackgroundLayer: (layerId: LayerId) => void;

    /**
     * Nastaví popisnou vrstvu.
     *
     * @param layerId - ID vrstvy nebo `null` pro její vypnutí.
     * @returns Akce.
     */
    setForegroundLayer: (layerId: LayerId) => void;

    /**
     * Nastaví viditelné datové vrstvy.
     *
     * @param layerIds - Pole ID vrstev.
     * @returns Akce.
     */
    setLayers: (layerIds: LayerId[]) => void;

    /**
     * Nastaví rozbalené uzly katalogu.
     *
     * @param nodeIds - Pole ID uzlů katalogu.
     * @returns Akce.
     */
    setCatalogNodes: (nodeIds: string[]) => void;

    /**
     * Nastaví pozici středu mapy.
     *
     * @param location - Souřadnice středu mapy.
     * @returns Akce.
     */
    setLocation: (location: Coordinate) => void;

    /**
     * Nastaví přiblížení mapy.
     *
     * @param zoom - Hodnota přiblížení.
     * @returns Akce.
     */
    setZoom: (zoom: number) => void;

    /**
     * Nastaví filtr obsahu datových vrstev.
     *
     * @param filter - Parametry filtru.
     * @returns Akce.
     */
    setFilter: (filter: FeatureFilter) => void;

    /**
     * Nastaví filtr zobrazení features podle typu filtru
     *
     * @param type - FeatureFilterType
     * @param layerId
     * @returns Ids features podle filtru
     */
    setLayerFeaturesFilter: (type: FeatureFilterType, layerId: LayerId) => void;

    /**
     * Nastaví datové vrstvy jako aktivní
     * @param active
     * @returns Akce
     */
    setDataLayersActive: (active: boolean) => void;

    /**
     * Nastaví vrstvu pro vkládání nově vytvořených {ol.Feature}
     * @param layer
     * @return layer - vrstva
     */
    setInsertionLayer: (layer: any) => void;

    /**
     * Změna nastavení tisku
     * @param option
     * @returns Akce
     */
    setPrintOptions: (option: FieldProps) => void;

    /**
     * Nasataví konfiguraci tisku
     * @param config
     * @returns
     */
    setPrintConfig: (config: PrintConfigType) => void;

    /**
     * Změna nastavení modu tisku
     * @param mode
     * @returns Akce
     */
    setPrintMode: (mode: any) => void;

    /**
     * Změna nastavení modu tisku
     * @param start
     * @returns Akce
     */
    setStartPrintProcess: (start: boolean) => void;

    /**
     * Přenačte vrstvu.
     *
     * @param layerId - ID vrstvy. Může obsahovat wildcard znak `*` pro specifikování masky vrstev.
     *                  Například `app:layer:*` přenačtě vrstvy `app:layer:1`, `app:layer:2` atd.
     * @param featureIds - ID features pro přenačtení v daných vrstvách. Při neuvedení jsou přenačteny celé vrstvy.
     * @param featureIdType - Typ ID features.
     * @returns Akce.
     */
    reloadLayer: (
        layerId: LayerId,
        featureIds?: FeatureId[],
        featureIdType?: FeatureIdType,
        tags?: LayerSelectTags,
    ) => void;

    /**
     * Provede zoom na vrstvu.
     * Vrstva je v případě nutnosti donačtena a zobrazena.
     *
     * @param layerId - ID vrstvy.
     * @param options - Volitelné parametry.
     * @returns Akce.
     */
    zoomOnLayer: (layerId: LayerId, options?: ZoomOptions) => void;

    /**
     * Přenačte detail feature.
     *
     * @param featureId - ID feature.
     * @param layerId - ID vrstvy. Může obsahovat wildcard znak `*` pro specifikování masky vrstev.
     *                  Například `app:layer:*` přenačtě vrstvy `app:layer:1`, `app:layer:2` atd.
     * @returns Akce.
     */
    reloadDetail: (featureId: FeatureId, layerId: LayerId, tags?: LayerSelectTags) => void;

    /**
     * Zavře aktivní detail feature.
     *
     * @param collapse - Zda zároveň skrýt i panel ve kterém je detail zobrazen.
     * @returns Akce.
     */

    closeDetail: (collapse: boolean) => void;

    /**
     * Aktivuje zobrazení objektu - preview.
     *
     * @param activated - Zobrazit objekt.
     * @param searchValue - Dodatečné parametry pro aktivaci nástroje.
     * @returns Akce.
     */
    activateObjectPreview: (activated: boolean, searchValue?: any) => void;

    /**
     * Aktivuje zobrazení středového kříže mapy.
     *
     * @returns Akce.
     */
    showCross: () => void;

    /**
     * Deaktivuje zobrazení středového kříže mapy.
     *
     * @returns Akce.
     */
    hideCross: () => void;

    /**
     * Nastaví kontextové parametry pro aplikační konektory.
     *
     * @param contextParams - nové kontextové parametry
     */
    setContextParams: (contextParams: Params) => void;

    /**
     * Pošle požadavek na změření objetků v dané vrstvě, nebo konkrétních features
     * @param layerIds - pole ID vrstev, undefined pokud se mají měřit podle předaných features
     * @param features - pokud je pole layerIds undefined, měříme podle předaných features
     * @returns
     */
    measureFeatures: (layerIds: LayerId[] | undefined, features: Feature[]) => void;

    /**
     * Vrátí konfiguraci vrstvy podle id.
     * @param layerId - ID vrstvy.
     */
    getLayerConfig: (layerId: LayerId) => void;
}

/**
 * Vytvoří akce nad mapovou komponentou
 *
 * @param dispatch - Dispatcher akcí.
 * @return Implementace akcí.
 * @ignore
 */
export const createMapActions = (dispatch: Dispatch<FrameAction>): MapActions => ({
    setTopic: (topic: string): void => dispatch(InternalActions.setProperty('topic', topic)),
    updateTopic: (topic?: string): void => dispatch(InternalActions.setProperty('updateTopic', topic)),
    setBackgroundLayer: (layerId: LayerId): void => dispatch(InternalActions.setProperty('backgroundLayer', layerId)),
    setForegroundLayer: (layerId: LayerId): void => dispatch(InternalActions.setProperty('foregroundLayer', layerId)),
    setLayers: (layerIds: LayerId[]): void => dispatch(InternalActions.setProperty('layers', layerIds)),
    setCatalogNodes: (nodeIds: string[]): void => dispatch(InternalActions.setProperty('catalogNodes', nodeIds)),
    setLocation: (location: Coordinate): void => dispatch(InternalActions.setProperty('location', location)),
    setZoom: (zoom: number): void => dispatch(InternalActions.setProperty('zoom', zoom)),
    setFilter: (filter: FeatureFilter): void => dispatch(InternalActions.setProperty('filter', filter)),
    setLayerFeaturesFilter: (type: FeatureFilterType, layerId: LayerId): void =>
        dispatch(InternalActions.setProperty('layerFeaturesFilter', {type, layerId})),
    setDataLayersActive: (active: boolean): void =>
        dispatch(InternalActions.setProperty('setDataLayersActive', active)),
    setInsertionLayer: (layer: any): void => dispatch(InternalActions.setProperty('setInsertionLayer', layer)),
    setPrintOptions: (option: FieldProps): void => dispatch(InternalActions.setProperty('setPrintOptions', option)),
    setPrintConfig: (config: PrintConfigType): void => dispatch(InternalActions.setProperty('setPrintConfig', config)),
    setPrintMode: (mode: any): void => dispatch(InternalActions.setProperty('setPrintMode', mode)),
    setStartPrintProcess: (start: boolean): void =>
        dispatch(InternalActions.setProperty('setStartPrintProcess', start)),

    activateObjectPreview: (activated: boolean, searchValue: any): void =>
        dispatch(FrameActions.postMessage({type: 'ACTIVATE_OBJECT_PREVIEW', activated, searchValue})),
    setContextParams: (contextParams: Params): void =>
        dispatch(InternalActions.setProperty('contextParams', contextParams)),
    measureFeatures: (layerIds: LayerId[] | undefined, features: Feature[]): void =>
        dispatch(FrameActions.postMessage({type: 'MEASURE_FEATURES', layerIds, features})),
    getLayerConfig: (layerId: LayerId): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'LAYERS_CONFIG',
                id: layerId,
            }),
        ),
    reloadLayer: (
        layerId: LayerId,
        featureIds?: FeatureId[],
        featureIdType?: FeatureIdType,
        tags?: LayerSelectTags,
    ): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'RELOAD_LAYER',
                id: layerId,
                featureIds,
                featureIdType,
                tags,
            }),
        ),

    zoomOnLayer: (layerId: LayerId, options: ZoomOptions = {}): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'LAYER_ZOOM',
                id: layerId,
                zoomPadding: options.padding,
                zoomRatio: options.zoomRatio,
                zoomMinScale: options.zoomMinScale,
            }),
        ),

    reloadDetail: (featureId: FeatureId, layerId: LayerId, tags?: LayerSelectTags): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'RELOAD_DETAIL',
                id: featureId,
                layerId,
                tags,
            }),
        ),

    closeDetail: (collapse: boolean): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'CLOSE_DETAIL',
                collapse,
            }),
        ),

    showCross: (): void => dispatch(FrameActions.postMessage({type: 'SHOW_CROSS'})),
    hideCross: (): void => dispatch(FrameActions.postMessage({type: 'HIDE_CROSS'})),
});
